import { useCallback, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Tabs } from '@antimatterhq/ui/components/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@antimatterhq/ui/components/dropdownMenu';
import { Icon } from '@antimatterhq/ui/components/icon';
import { QueryTabItem } from './QueryTabItem';
import { QueryOpenExistingDialog } from './QueryOpenExistingDialog';
import {
  queryTabIdsAtom,
  createNewQueryTabAtom,
  recentQueriesAtom,
  openQueryTabAtom,
} from '@/atoms/query/queryAtoms';

export function QueryTabList() {
  const [tabIds] = useAtom(queryTabIdsAtom);
  const createTab = useSetAtom(createNewQueryTabAtom);
  const recentQueries = useAtomValue(recentQueriesAtom);
  const openTab = useSetAtom(openQueryTabAtom);
  const [showOpenExistingDialog, setShowOpenExistingDialog] = useState(false);

  // Get non-open recent queries
  const recentNonOpenQueries = recentQueries
    .filter((q) => !tabIds.includes(q.id))
    .slice(0, 5); // Show max 5 recent queries

  const handleOpenRecent = useCallback(
    (queryId: string) => {
      openTab(queryId);
    },
    [openTab],
  );

  const handleNewQuery = useCallback(() => {
    createTab();
  }, [createTab]);

  const handleOpenExisting = useCallback(() => {
    setShowOpenExistingDialog(true);
  }, []);

  return (
    <>
      <Tabs.List data-testid="query-tab-list">
        {tabIds.map((tabId, index) => (
          <QueryTabItem key={tabId} tabId={tabId} tabIndex={index} />
        ))}

        {/* If no recent queries, just use the regular add button */}
        {recentQueries.length === 0 ? (
          <Tabs.Add onClick={handleNewQuery} data-testid="query-tab-add" />
        ) : (
          /* Dropdown menu for add button when there are recent queries */
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Tabs.Add data-testid="query-tab-add" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem
                onClick={handleNewQuery}
                data-testid="new-query-menu-item"
              >
                <Icon icon="lucide:plus" className="mr-2 h-4 w-4" />
                New query
              </DropdownMenuItem>

              {/* Show recent queries if available */}
              {recentNonOpenQueries.length > 0 && (
                <>
                  <DropdownMenuItem onClick={handleOpenExisting}>
                    <Icon icon="lucide:folder-open" className="mr-2 h-4 w-4" />
                    Open existing
                  </DropdownMenuItem>
                  <div className="bg-border my-1 h-px" />
                  <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                    Recent
                  </div>
                  {recentNonOpenQueries.map((query) => (
                    <DropdownMenuItem
                      key={query.id}
                      onClick={() => handleOpenRecent(query.id)}
                    >
                      <Icon
                        icon="lucide:folder-open"
                        className="mr-2 h-4 w-4"
                      />
                      <span className="truncate">{query.name}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              {/* Only show Open existing if no recent queries */}
              {recentNonOpenQueries.length === 0 &&
                recentQueries.length > 0 && (
                  <DropdownMenuItem onClick={handleOpenExisting}>
                    <Icon icon="lucide:folder-open" className="mr-2 h-4 w-4" />
                    Open existing
                  </DropdownMenuItem>
                )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </Tabs.List>

      {/* Open existing dialog */}
      <QueryOpenExistingDialog
        open={showOpenExistingDialog}
        onOpenChange={setShowOpenExistingDialog}
      />
    </>
  );
}
